import { logger } from '../../utils/logger';
import { MatomoInstance } from './types';

export type TrackerCommand = ((tracker: MatomoInstance) => void) | (string | number | boolean)[];
export type TrackerQueueType = 'config' | 'pageview' | undefined;

export class Tracker {
    private tracker: any = null;
    private commandsCache: {command: TrackerCommand; type?: TrackerQueueType}[] = [];
    private enabled = false;
    private debugLabel = '';

    constructor(config: {
        debugLabel: string;
        url: string;
        siteId: string | number;
        awaitMatomo: Promise<MatomoInstance>;
    }) {
        this.debugLabel = `[${config.debugLabel}]`;

        logger.debug('tracking', this.debugLabel, 'Waiting for Matomo being ready');
        (async () => {
            const matomo = await config.awaitMatomo;
            this.tracker = matomo.getTracker(config.url, config.siteId);

            logger.debug('tracking', this.debugLabel, 'Initialized tracker');

            this.flushCommands();
        })();
    }

    getTracker() {
        return this.tracker;
    }

    enable() {
        logger.debug('tracking', this.debugLabel, 'Enabled');
        this.enabled = true;
        this.flushCommands();
    }

    push(commands: TrackerCommand[], type?: TrackerQueueType) {
        logger.debug('tracking', this.debugLabel, `Processing ${commands.length} commands of type "${type}"`);
        logger.debug('tracking', this.debugLabel, commands);

        const mappedCommands = commands.map((command) => ({command, type}));

        if (this.canFlushCommands()) {
            logger.debug('tracking', this.debugLabel, 'Commands can be flushed, so no caching is necessary');
            this.commandsCache.push(...mappedCommands);
            this.flushCommands();
            return;
        }

        switch (type) {
            case 'config':
                logger.debug('tracking', this.debugLabel, 'Commands are config commands and therefore will be queued without any limits');
                this.commandsCache.push(...mappedCommands);
                break;
            case 'pageview':
                logger.debug('tracking', this.debugLabel, 'Commands are pageview command and therefore only the last set will be kept in cache');
                this.commandsCache = this.commandsCache.filter((entry) => entry.type !== type);
                this.commandsCache.push(...mappedCommands);
                break;
            case undefined:
                logger.debug('tracking', this.debugLabel, 'Commands have no type and therefore won\'t be cached at all');
                break;
            default:
                logger.error('tracking', this.debugLabel, `Commands have unknown type "${type}" and therefore won't be cached at all`);
                break;
        }

        this.flushCommands();
    }

    private canFlushCommands() {
        return this.tracker && this.enabled;
    }

    private flushCommands() {
        if (!this.canFlushCommands() || !this.commandsCache.length) {
            return;
        }

        logger.debug('tracking', this.debugLabel, `Flushing ${this.commandsCache.length} commands`);
        logger.debug('tracking', this.debugLabel, this.commandsCache);

        const commands = this.commandsCache.slice();
        this.commandsCache = [];

        commands.forEach((entry) => {
            if (entry.command instanceof Array) {
                const [method, ...args] = entry.command;
                if ((typeof method) !== 'string' || !this.tracker[method as string]) {
                    logger.error('tracking', this.debugLabel, `Ignoring invalid tracking method: ${method}`);
                    return;
                }

                this.tracker[method as string](...args);
            } else {
                entry.command(this.tracker);
            }
        });
    }
}
